import * as React from 'react';

import { Avatar, Box, Button, Typography } from '@mui/material';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    FaOptionIcon,
    mailIcon,
    refreshDataIcon,
    refusedIcon,
    reminderIcon,
    sendIcon,
} from '@europrocurement/l2d-icons';
import {
    ChipStatus,
    FlexyIconButton,
    ShortenDisplay,
    statusConfig,
    ToolbarProps,
    useFullLayout,
    useL2DTheme,
    VerticalDivider,
} from '@europrocurement/flexy-components';

import {
    AchatsAppDispatch,
    FactureAchatApiObject,
    ReminderPublisher,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { ReminderPublisherApiRegenerateMailContentReminderPublisherItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import useDeleteReminderPublisher from '../hooks/useDeleteReminderPublisher';
import useSendMailReminderPublisher from '../hooks/useSendMailReminderPublisher';

/**
 * Props for SupplierInfoCard component
 */
type SupplierInfoCardProps = {
    /** Reminder publisher data */
    reminderPublisher?: ReminderPublisher;
    fetchInvoices?: getDataThunkType<FactureAchatApiObject>;
    /** Callback pour rafraîchir le contenu du mail après régénération */
    onRefreshMailContent?: () => void;
};

type SubTitleElements = Array<{
    icon: IconProp;
    label?: string;
    onClick?: () => void;
}>;

/**
 * Component for displaying supplier information
 *
 * Shows supplier name, email and reminder status
 */
const SupplierInfoCard: React.FunctionComponent<SupplierInfoCardProps> = function ({
    reminderPublisher,
    fetchInvoices,
    onRefreshMailContent,
}) {
    const { palette } = useL2DTheme();
    const { setPageTitle, clearPageTitle } = useFullLayout();
    const publisherEmail = reminderPublisher?.fournisseurEmail || 'Non spécifié';
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    React.useEffect(() => {
        const name = reminderPublisher?.fournisseurNom;
        const status = statusConfig[reminderPublisher?.status as keyof typeof statusConfig].label;

        if (!name) return undefined;

        setPageTitle(status ? `Rappel de ${name} | ${status}` : `Rappel de ${name}`);

        return () => clearPageTitle();
    }, [
        reminderPublisher?.fournisseurNom,
        reminderPublisher?.status,
        setPageTitle,
        clearPageTitle,
    ]);

    const headerStyle = { display: 'flex', alignItems: 'center', padding: '10px' };
    const avatarSize = '75px';
    const avatarStyle = {
        borderRadius: '8px',
        backgroundColor: palette.icon?.warning?.backgroundColor,
        minHeight: avatarSize,
        minWidth: avatarSize,
        marginRight: '20px',
    };
    const avatarIconStyle = {
        opacity: 1,
        color: palette.warning.main,
    };
    const nameStyle = {
        fontSize: '24px',
        fontWeight: '500',
        marginRight: '10px',
        width: 'auto',
    };
    const billingCodeStyle = { ml: 2, fontWeight: '400' };
    const subtitleElementStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '300',
        marginRight: '10px',
        color: 'inherit',
    };

    const { selectReminderPublisher, reminderPublisherApi } = useAchatsService();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<AchatsAppDispatch>();

    /**
     * Gère le rafraîchissement complet : données + régénération du contenu du mail
     */
    const handleRefresh = async () => {
        if (isRefreshing) return;

        setIsRefreshing(true);

        try {
            // 1. Régénération du contenu du mail
            if (reminderPublisher?.id) {
                const requestParams: ReminderPublisherApiRegenerateMailContentReminderPublisherItemRequest =
                    {
                        id: reminderPublisher.id,
                        reminderPublisherJsonldReminderPublisherWriteTimestampableBlameambleGroupSocieteAwareGroup:
                            reminderPublisher,
                        xIdSociete,
                    };

                await reminderPublisherApi.regenerateMailContentReminderPublisherItem(
                    requestParams,
                );
            }

            // 2. Rafraîchissement des données
            if (fetchInvoices) {
                dispatch(fetchInvoices({}));
            }
            if (reminderPublisher?.id) {
                dispatch(selectReminderPublisher({ id: reminderPublisher.id }));
            }

            // 3. Rafraîchissement du contenu du mail avec un délai
            setTimeout(() => {
                if (onRefreshMailContent) {
                    onRefreshMailContent();
                }
                setIsRefreshing(false);
            }, 1500);
        } catch (error) {
            console.error('Erreur lors du rafraîchissement:', error);
            setIsRefreshing(false);
        }
    };

    const { deleteReminderPublisher } = useDeleteReminderPublisher({ from: 'details' });
    const { sendMailReminderPublisher } = useSendMailReminderPublisher({ from: 'details' });

    const actions: ToolbarProps['actions'] = [
        {
            onClick: handleRefresh,
            tooltip: isRefreshing
                ? 'Rafraîchissement en cours...'
                : 'Rafraîchir les données et régénérer le mail',
            icon: {
                prefix: 'fasl',
                iconName: (refreshDataIcon.props.icon as IconDefinition).iconName,
            },
            disabled: isRefreshing,
        },
        {
            onClick: () => {
                if (!reminderPublisher) return;

                deleteReminderPublisher(reminderPublisher);
            },
            tooltip: 'Annuler le rappel',
            icon: {
                prefix: 'fasl',
                iconName: (refusedIcon.props.icon as IconDefinition).iconName,
            },
            disabled: isRefreshing,
        },
        {
            onClick: () => {
                if (!reminderPublisher) return;

                sendMailReminderPublisher(reminderPublisher);
            },
            tooltip: 'Envoyer le rappel',
            icon: {
                prefix: 'fasl',
                iconName: (sendIcon.props.icon as IconDefinition).iconName,
            },
            disabled: isRefreshing,
        },
    ];

    const subtitleElements: SubTitleElements = [
        {
            icon: mailIcon.props.icon,
            label: publisherEmail,
        },
    ];

    const labelFontSize = '13px';

    return (
        <Box
            sx={{
                marginTop: 1,
                marginBottom: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}
        >
            <Box
                sx={headerStyle}
                data-testid="PrescriberHeader-test-id"
            >
                <Box>
                    <Avatar sx={avatarStyle}>
                        <FaOptionIcon
                            icon={reminderIcon.props.icon}
                            sx={avatarIconStyle}
                        />
                    </Avatar>
                </Box>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            marginBottom: '10px',
                        }}
                    >
                        <Typography sx={nameStyle}>
                            Rappel de {reminderPublisher?.fournisseurNom || 'Fournisseur inconnu'}
                        </Typography>
                        <Typography sx={{ display: 'flex', flexDirection: 'row' }}>
                            <span>(</span>
                            <span>
                                <ShortenDisplay text={reminderPublisher?.id} />
                            </span>
                            <span>)</span>
                        </Typography>
                        <Typography sx={billingCodeStyle}>
                            <ChipStatus status={reminderPublisher?.status || 'Non spécifié'} />
                        </Typography>
                    </Box>
                    <Box display="flex">
                        {subtitleElements.map(({ icon, label, onClick }) => {
                            if (!label) {
                                return null;
                            }

                            if (onClick && typeof onClick === 'function') {
                                return (
                                    <Button
                                        key={label}
                                        onClick={onClick}
                                        sx={subtitleElementStyle}
                                    >
                                        <FaOptionIcon
                                            icon={icon}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginRight: '4px',
                                            }}
                                        />
                                        <Typography sx={{ fontSize: labelFontSize }}>
                                            {label}
                                        </Typography>
                                    </Button>
                                );
                            }
                            return (
                                <Box
                                    key={label}
                                    sx={subtitleElementStyle}
                                >
                                    <FaOptionIcon
                                        icon={icon}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginRight: '4px',
                                        }}
                                    />
                                    <Typography sx={{ fontSize: labelFontSize }}>
                                        {label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{}}
            >
                <VerticalDivider sx={{ margin: '0px 10px' }} />
                {actions && actions.length > 0 && (
                    <>
                        {actions.map((action) => (
                            <FlexyIconButton
                                key={action.tooltip?.toString() || action?.icon?.toString()}
                                {...action}
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default SupplierInfoCard;
